import * as Label from "@radix-ui/react-label";
import { type InputHTMLAttributes, type ReactNode, forwardRef, useId } from "react";
import { cn } from "./cn";

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
  trailing?: ReactNode;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(
  ({ label, hint, error, trailing, id, className, ...props }, ref) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    const hintId = hint ? `${inputId}-hint` : undefined;
    const errorId = error ? `${inputId}-err` : undefined;
    return (
      <div className="flex flex-col gap-1">
        <Label.Root htmlFor={inputId} className="text-xs font-medium text-[var(--fg-muted)]">
          {label}
        </Label.Root>
        <div className="relative flex items-center">
          <input
            id={inputId}
            ref={ref}
            aria-invalid={error ? true : undefined}
            aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
            className={cn(
              "h-9 w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)]/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus)] aria-[invalid=true]:border-[var(--danger)]",
              trailing && "pr-10",
              className,
            )}
            {...props}
          />
          {trailing && <div className="absolute right-2 flex items-center">{trailing}</div>}
        </div>
        {hint && !error && (
          <p id={hintId} className="text-xs text-[var(--fg-muted)]">
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} className="text-xs text-[var(--danger)]">
            {error}
          </p>
        )}
      </div>
    );
  },
);
Field.displayName = "Field";
